import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import BoltIcon from "@mui/icons-material/Bolt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";

import {
  StatCard,
  LineChart,
  DonutChart,
  CombinedBarLineChart,
} from "../../../../../shared/components/charts";
import { DashboardCard } from "../../../../../shared/components/DashboardCard";
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  DashboardCardSkeleton,
  CohortGuard,
} from "../../../../../shared/components";
import type {
  MetricStatValue,
  PatternDataPoint,
} from "../../../../../shared/api/types";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
} from "../../../../../shared/hooks";
import {
  useStrainActivityStats,
  useStrainActivityTrends,
  useHeartRateZonesDistribution,
  useStrainPatterns,
  useRecoveryPatterns,
} from "../hooks";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
} from "../../shared/utils";

const fallbackStats: Record<string, MetricStatValue> = {
  day_strain: {
    label: "Avg Day Strain",
    value: 0,
    unit: null,
    change: "+00%",
    previous_period_value: 0,
    rag_label: "Good",
    rag_color: "green",
    rag_insight: "Average day strain on a 0-21 scale (higher indicates more load).",
    additional_fields: { max: 21 },
  },
  active_time: {
    label: "Avg Active Time",
    value: 0,
    unit: "min",
    change: "+00%",
    previous_period_value: 0,
    rag_label: "Good",
    rag_color: "green",
    rag_insight:
      "Average active time in minutes derived from time spent in heart rate zones during workouts.",
  },
  calories_burn: {
    label: "Avg Calories Burned",
    value: 0,
    unit: "kcal",
    change: "+00%",
    previous_period_value: 0,
    rag_label: "Good",
    rag_color: "green",
    rag_insight:
      "Average calories burned in kilocalories (kcal), derived from energy in kJ.",
  },
};

const STRAIN_AND_ACTIVITY_DETAILS = {
  strainAndActivity: `Displays daily fluctuations in average strain and activity levels across the organisation, illustrating patterns of physical exertion over time. Strain reflects overall cardiovascular load and intensity of effort, while activity represents movement volume and duration.

Together, these trends provide insight into how consistently employees are engaging in physical activity, periods of elevated exertion, and how overall workload may influence recovery balance. Monitoring these patterns helps identify sustained high-load periods, potential fatigue risk, and broader shifts in organisational activity behaviour.`,
  heartRateZones: `Shows the distribution of time spent in different heart rate intensity zones, from low to high effort. This reflects overall training load and how exercise intensity is managed.

By analysing time spent in each zone, it provides insight into cardiovascular demand, effort distribution, and whether activity levels are predominantly moderate, vigorous, or high intensity.`,
  weeklyStrainPatterns: `Compares daily strain and recovery percentages across the week to assess workload distribution and recovery balance. Strain represents cumulative cardiovascular and physiological load, while recovery reflects the body’s readiness and restorative status.

By examining these patterns across weekdays and weekends, this view highlights periods of sustained high exertion, insufficient recovery, or improved balance. It supports identification of workload clustering, potential fatigue risk, and opportunities to optimise recovery timing to maintain performance and wellbeing throughout the week.`,
};

export const StrainAndActivity: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } =
    usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [trendView, setTrendView] = useState("day_strain");

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId),
    [filterState, dateRange, effectiveClientId]
  );

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      day_strain: {
        min: typeof metric_min.day_strain === "number" ? metric_min.day_strain : undefined,
        max: typeof metric_max.day_strain === "number" ? metric_max.day_strain : undefined,
      },
      cardio_load: {
        min: typeof metric_min.cardio_load === "number" ? metric_min.cardio_load : undefined,
        max: typeof metric_max.cardio_load === "number" ? metric_max.cardio_load : undefined,
      },
      heart_rate: {
        min: typeof metric_min.heart_rate === "number" ? metric_min.heart_rate : undefined,
        max: typeof metric_max.heart_rate === "number" ? metric_max.heart_rate : undefined,
      },
      calories_burn: {
        min: typeof metric_min.calories_burn === "number" ? metric_min.calories_burn : undefined,
        max: typeof metric_max.calories_burn === "number" ? metric_max.calories_burn : undefined,
      },
    };
  }, [overallRangeQuery.data]);

  const statsQuery = useStrainActivityStats(filterParams);

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;
  const trendsQuery = useStrainActivityTrends({
    ...filterParams,
    metrics: ["day_strain", "cardio_load", "heart_rate", "calories_burn"],
    granularity,
  });
  const distributionQuery = useHeartRateZonesDistribution(filterParams);
  const strainPatternsQuery = useStrainPatterns({
    ...filterParams,
    metric: "strain_patterns",
    granularity: "weekly",
  });
  const recoveryPatternsQuery = useRecoveryPatterns({
    ...filterParams,
    metric: "recovery_patterns",
    granularity: "weekly",
  });

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const trendsData = useMemo(() => {
    const transformed = transformTrendsData(trendsQuery.data, [
      "day_strain",
      "cardio_load",
      "heart_rate",
      "calories_burn",
    ]);
    return transformed.map((item) => {
      const cleaned: Record<string, string | number> = {};
      Object.keys(item).forEach((key) => {
        const value = item[key];
        if (value !== null) {
          cleaned[key] = value;
        }
      });
      return cleaned;
    });
  }, [trendsQuery.data]);

  const distributionData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    return distributionQuery.data.data.distribution.map((item) => ({
      label: item.label,
      value: item.value,
      color: item.label.includes("Zone 1-3")
        ? theme.palette.success.main
        : theme.palette.error.main,
    }));
  }, [distributionQuery.data, theme]);

  const patternsData = useMemo(() => {
    const strainPatterns = strainPatternsQuery.data?.data?.patterns || [];
    const recoveryPatterns = recoveryPatternsQuery.data?.data?.patterns || [];

    if (strainPatterns.length === 0 && recoveryPatterns.length === 0) return [];

    const getPatternLabel = (pattern: PatternDataPoint) =>
      pattern.day ?? pattern.time ?? "";

    const strainMap = new Map(
      strainPatterns.map((pattern) => [
        getPatternLabel(pattern),
        pattern.value,
      ])
    );
    const recoveryMap = new Map(
      recoveryPatterns.map((pattern) => [
        getPatternLabel(pattern),
        pattern.value,
      ])
    );

    const allLabels = new Set([
      ...strainPatterns.map((pattern) => getPatternLabel(pattern)),
      ...recoveryPatterns.map((pattern) => getPatternLabel(pattern)),
    ]);

    return Array.from(allLabels)
      .filter((label) => label)
      .map((label) => ({
        day: label!,
        strain: strainMap.get(label) || 0,
        recovery: recoveryMap.get(label) || 0,
      }));
  }, [strainPatternsQuery.data, recoveryPatternsQuery.data]);

  const statCards = useMemo(() => {
    const stats = statsQuery.data?.data;
    return [
      {
        metric: stats?.day_strain || fallbackStats.day_strain,
        icon: BoltIcon,
      },
      {
        metric: stats?.active_time || fallbackStats.active_time,
        icon: AccessTimeIcon,
      },
      {
        metric: stats?.calories_burn || fallbackStats.calories_burn,
        icon: LocalFireDepartmentIcon,
      },
    ];
  }, [statsQuery.data]);

  const hasTrendDataForSelectedMetric = useMemo(
    () =>
      trendsData.some((item) => {
        const value = item[trendView];
        return typeof value === "number" && Number.isFinite(value);
      }),
    [trendsData, trendView]
  );

  const getTrendLabel = () => {
    switch (trendView) {
      case "day_strain":
        return "Day Strain";
      case "cardio_load":
        return "Cardio Load";
      case "heart_rate":
        return "Heart Rate";
      case "calories_burn":
        return "Calories Burn";
      default:
        return "Day Strain";
    }
  };

  const getYAxisMax = () => {
    switch (trendView) {
      case "day_strain":
        return 21;
      case "cardio_load":
        return undefined;
      case "heart_rate":
        return undefined;
      case "calories_burn":
        return undefined;
      default:
        return 21;
    }
  };

  const isLoading =
    statsQuery.isLoading ||
    trendsQuery.isLoading ||
    distributionQuery.isLoading ||
    strainPatternsQuery.isLoading ||
    recoveryPatternsQuery.isLoading;

  const hasError =
    statsQuery.isError ||
    trendsQuery.isError ||
    distributionQuery.isError ||
    strainPatternsQuery.isError ||
    recoveryPatternsQuery.isError;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
        />

        {hasError && (
          <Alert severity="error">
            Failed to load strain and activity data. Please try again later.
          </Alert>
        )}

        <Grid container spacing={3}>
          {isLoading ? (
            <>
              {[1, 2, 3].map((index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                  <DashboardCardSkeleton />
                </Grid>
              ))}
            </>
          ) : (
            statCards.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <StatCard metric={stat.metric} icon={stat.icon} />
              </Grid>
            ))
          )}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Avg Strain and Activity Trends"
              subtitle="Daily fluctuations in strain levels, highlighting overall exertion and recovery balance."
              details={STRAIN_AND_ACTIVITY_DETAILS.strainAndActivity}
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setTrendView(newValue);
                  }}
                  size="small"
                >
                  <ToggleButton value="day_strain">Day Strain</ToggleButton>
                  <ToggleButton value="cardio_load">Cardio Load</ToggleButton>
                  <ToggleButton value="heart_rate">Heart Rate</ToggleButton>
                  <ToggleButton value="calories_burn">
                    Calories Burn
                  </ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : trendsData.length > 0 && hasTrendDataForSelectedMetric ? (
                  <LineChart
                    data={trendsData}
                    xAxisKey="label"
                    series={[
                      {
                        dataKey: trendView,
                        label: getTrendLabel(),
                        color: theme.palette.warning.main,
                        area: true,
                      },
                    ]}
                    height={300}
                    yAxisMin={0}
                    yAxisMax={getYAxisMax()}
                    showMinMaxLines
                    referenceMin={overallMinMax[trendView as keyof typeof overallMinMax]?.min}
                    referenceMax={overallMinMax[trendView as keyof typeof overallMinMax]?.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">
                      No {getTrendLabel().toLowerCase()} data available for the
                      selected filters.
                    </Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard
              title="Heart Rate Zones"
              subtitle="Distribution of time spent across heart rate intensity zones, reflecting training load and exertion control."
              details={STRAIN_AND_ACTIVITY_DETAILS.heartRateZones}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : distributionData.length > 0 ? (
                  <DonutChart data={distributionData} height={300} />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No distribution data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard
              title="Weekly Strain Patterns"
              subtitle="Comparison of daily strain and recovery percentages across the week to track workload balance."
              details={STRAIN_AND_ACTIVITY_DETAILS.weeklyStrainPatterns}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : patternsData.length > 0 ? (
                  <CombinedBarLineChart
                    data={patternsData}
                    xAxisKey="day"
                    barSeries={[
                      {
                        dataKey: "strain",
                        label: "Day Strain",
                        color: theme.palette.primary.main,
                      },
                    ]}
                    lineSeries={[
                      {
                        dataKey: "recovery",
                        label: "Recovery %",
                        color: theme.palette.success.main,
                      },
                    ]}
                    height={300}
                    barYAxisMin={0}
                    barYAxisMax={21}
                    lineYAxisMin={0}
                    lineYAxisMax={100}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
